import { useEffect, useState } from "react";
import { SketchPicker } from "react-color";
import { useTranslation } from "react-i18next";
import { RemoveAction } from "../../Common/_components";
import { FileUploadComponent } from "../../Everou/_components";

type Props = {
    customization: any;
    isUploading: boolean;
    onChangeAccentColor: (hex: string) => void;
    onChangeHeaderColor: (hex: string) => void;
    onResetAccentColor: () => void;
    onResetHeaderColor: () => void;
    onUploadLogo: (file: any) => void;
    onRemoveLogo: () => void;
};

export function CustomizationTemplateEditor({
    customization,
    isUploading,
    onChangeAccentColor,
    onChangeHeaderColor,
    onResetAccentColor,
    onResetHeaderColor,
    onUploadLogo,
    onRemoveLogo,
}: Props) {
    const { t } = useTranslation();

    const [accentColor, setAccentColor] = useState(
        (customization && customization.accentColor) || "#000000",
    );
    const [headerColor, setHeaderColor] = useState(
        (customization && customization.headerColor) || "#000000",
    );

    useEffect(() => {
        setAccentColor(
            (customization && customization.accentColor) || "#000000",
        );
        setHeaderColor(
            (customization && customization.headerColor) || "#000000",
        );
    }, [customization]);

    function handleResetAccentColor() {
        setAccentColor("#000000");
        onResetAccentColor();
    }

    function handleResetHeaderColor() {
        setHeaderColor("#000000");
        onResetHeaderColor();
    }

    return (
        <div>
            {headerColorSelectorSection()}
            <div className="mt-3" />
            {accentColorSelectorSection()}
            <div className="mt-3" />

            {imageUploadSection(
                t("mod_customization_location_logo"),
                customization && customization.logo,
                onUploadLogo,
                onRemoveLogo,
            )}

        </div>
    );

    function headerColorSelectorSection() {
        return (
            <div>
                {titleFn(
                    t("mod_customization_header_color"),
                    !!(customization && customization.headerColor),
                    handleResetHeaderColor,
                )}
                <div className="d-flex justify-content-center">
                    <SketchPicker
                        color={headerColor}
                        onChangeComplete={(color) =>
                            onChangeHeaderColor(color.hex)
                        }
                        onChange={(color) => setHeaderColor(color.hex)}
                        presetColors={[]}
                        disableAlpha
                    />
                </div>
            </div>
        );
    }

    function accentColorSelectorSection() {
        return (
            <div>
                {titleFn(
                    t("mod_customization_accent_color"),
                    !!(customization && customization.accentColor),
                    handleResetAccentColor,
                )}
                <div className="mt-1" />
                <div className="d-flex justify-content-center my-2 cp">
                    <SketchPicker
                        color={accentColor}
                        onChangeComplete={(color) =>
                            onChangeAccentColor(color.hex)
                        }
                        onChange={(color) => setAccentColor(color.hex)}
                        presetColors={[]}
                        disableAlpha
                    />
                </div>
            </div>
        );
    }

    function imageUploadSection(title, img, handleUpload, handleRemove) {
        return (
            <div>
                {titleFn(title)}
                <div className="mt-1" />
                <FileUploadComponent
                    onUpload={handleUpload}
                    isUploading={isUploading}
                    fileName={img ? t("global_image_uploaded") : null}
                    onRemove={handleRemove}
                    preview={img &&
                        <div className="d-flex justify-content-center align-items-center">
                            <img
                                className="mw-100 c-mh-15rem rounded border bg-light p-2"
                                alt={title}
                                src={img}
                            />
                        </div>
                    }
                />
            </div>
        );
    }

    // The colour sections still use the remove action here (to reset the
    // colour); the image ones don't, since removing the file now lives inside
    // the upload control itself.
    function titleFn(title, canRemove?, handleRemove?) {
        return (
            <div className="d-flex justify-content-between align-items-center">
                <div className="font-weight-bold text-uppercase">{title}</div>
                <RemoveAction
                    label={t("global_remove")}
                    isVisible={!!canRemove}
                    onClick={handleRemove}
                />
            </div>
        );
    }
}
